import { FC, useEffect, useState } from "react"
import { Score } from './Score';
import { TeamCard } from "./TeamCard";
import { Lineups } from "./Lineups";
import { Substitutions } from "./Substitutions";
import { MatchInfo } from "../../types/fixtures/match";
import { Links } from "./Links";
import { Goals } from "./Goals";
import { PenaltySeries } from "./PenaltySeries";
import PageTemplate from "../../components/layout/PageTemplate";
import { getLegacyId, getStaticUrl, getUrlByContext, setDocumentTitle } from "../../utils";
import { useParams } from "react-router";
import { getFixtureById } from "../../api/transport/common";
import { Loader } from "../../components/Loader";
import { Ball } from "./MatchIcons";
import { MatchStatus } from "../../types/stats";
import { Coaches } from "./Coaches";
import { Link } from "../../components";
import { useConfiguration } from "../../hooks/useConfiguration";
import { SiteType } from "../../types/common";

export const MatchCenterPage: FC = () => {
    const config = useConfiguration();

    const [matchData, setMatchData] = useState<MatchInfo>();

    const { matchId } = useParams();

    useEffect(() => {
        if (!matchId || isNaN(+matchId)) {
            return;
        }

        let legacyId = +matchId;
        if (config.site === SiteType.LEGACY_ACADEMY) {
            legacyId += 100000;
        }

        getFixtureById(legacyId).then(data => setMatchData(data));
    }, [matchId, config.site]);

    useEffect(() => {
        if (!matchData) {
            setDocumentTitle(`Протокол матча - Официальный сайт ФК «Родина»`);
            return;
        }
        setDocumentTitle(`${new Date(matchData.date).toLocaleDateString()} - ${matchData?.homeTeam?.name} - ${matchData?.guestTeam?.name} - ${matchData?.tournament?.name} - Официальный сайт ${config.site === SiteType.LEGACY_ACADEMY ? "Академия" : "ФК"} «Родина»`);
    }, [matchData, config.site])

    const bgUrl = 'https://fcrodina.com/static/media/app-page-bg.79e35420a8ad7ba901c0.png';

    const pageBgClassname = ` bg-[url(https://fcrodina.com/static/media/app-page-bg.79e35420a8ad7ba901c0.png)]`;

    if (!matchData) {
        return (
            <PageTemplate bgImageUrl={bgUrl} className={pageBgClassname}>
                <div className="min-h-screen">
                    <Loader size="large" color="#fff" />
                </div>
            </PageTemplate>
        );
    }

    const homeGoals = matchData?.state?.goals?.filter(g => g.teamId === matchData.homeTeam.id);
    const guestGoals = matchData?.state?.goals?.filter(g => g.teamId === matchData.guestTeam.id);

    const homePlayers = matchData?.state?.players?.filter(g => g.teamId === matchData.homeTeam.id && !g.isSub);
    const homeSubs = matchData?.state?.players?.filter(g => g.teamId === matchData.homeTeam.id && g.isSub);
    const guestPlayers = matchData?.state?.players?.filter(g => g.teamId === matchData.guestTeam.id && !g.isSub);
    const guestSubs = matchData?.state?.players?.filter(g => g.teamId === matchData.guestTeam.id && g.isSub);

    const homeCoach = matchData?.state?.coaches?.find(c => c.teamId === matchData.homeTeam.id);
    const guestCoach = matchData?.state?.coaches?.find(c => c.teamId === matchData.guestTeam.id);

    const noData = !matchData?.state || (matchData.status === MatchStatus.FINISHED && matchData.state.score === '-:-');

    return (
        <PageTemplate /*bgImageUrl={bgUrl} className={pageBgClassname}*/>
            <div className="flex flex-col justify-center w-full max-w-6xl mx-auto gap-4 text-slate-900 pt-8 pb-12 sm:pt-12 sm:pb-20 min-h-full" >
                <div className="text-center p-3 rounded">
                    <div className="sm:text-2xl"><Link to={`/games/?sezon=${getLegacyId(matchData?.tournament?.season?.legacyId)}&turnir=${getLegacyId(matchData?.tournament?.legacyId)}`}>{matchData?.tournament?.name}</Link></div>
                    <div>{new Date(matchData?.date).toLocaleString('ru-Ru', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric'
                    })}</div>
                </div>
                <Links
                    reportUrl={matchData?.links?.reportUrl}
                    videoUrl={matchData?.links?.videoUrl}
                    textLiveUrl={matchData?.links?.textLiveUrl}
                    reviewUrl={matchData?.links?.reviewUrl}
                    matchStatus={matchData?.status}
                />
                <div className="flex flex-row mx-auto w-full justify-around items-start sm:gap-16">
                    <div className="flex flex-col gap-4 w-32 sm:w-80 justify-start">
                        <TeamCard title={matchData.homeTeam.name} logoUrl={matchData.homeTeam.logo} />
                    </div>
                    <div className="flex flex-col justify-center min-h-24 sm:min-h-52">
                        {matchData.status === MatchStatus.СANCELED && <div className="text-orange-500 text-center text-lg sm:text-2xl font-bold">
                            ОТМЕНЁН
                        </div>}
                        {noData && matchData.status === MatchStatus.FINISHED && <div className="text-blue-500 text-center text-lg sm:text-2xl font-bold">
                            ЖДЁМ РЕЗУЛЬТАТА
                        </div>}
                        {matchData.status === MatchStatus.UPCOMING && <Score
                            score="-:-"
                        />}
                        {!noData && matchData.status !== MatchStatus.СANCELED && <Score
                            score={matchData?.state?.score || "-:-"}
                            firstHalfScore={matchData?.state?.halfTimeScore} />}
                    </div>
                    <div className="flex flex-col gap-4 w-32 sm:w-80 justify-start">
                        <TeamCard title={matchData.guestTeam.name} logoUrl={matchData.guestTeam.logo} />
                    </div>
                </div>
                {matchData?.state && !noData && <>
                    {!!matchData.state.goals.length && <div className="flex flex-row mx-auto w-full sm:text-2xl gap-3 sm:gap-8 justify-center items-start">
                        <div className="flex flex-col gap-4 w-1/2 justify-end">
                            <div className="flex flex-col gap-3">
                                <Goals goals={homeGoals} align="right" />
                            </div>
                        </div>
                        <div className="pt-1">
                            <Ball />
                        </div>
                        <div className="flex flex-col gap-4 w-1/2 justify-start">
                            <div className="flex flex-col align-end gap-3">
                                <Goals goals={guestGoals} align="left" />
                            </div>
                        </div>
                    </div>}
                    <div className="max-w-[1024px] w-full mx-auto flex flex-col sm:gap-8">
                        {!!matchData?.state?.penalties?.length && <PenaltySeries
                            homePenalties={matchData?.state?.penalties.filter(g => g.teamId === matchData.homeTeam.id)}
                            awayPenalties={matchData?.state?.penalties.filter(g => g.teamId === matchData.guestTeam.id)}
                        />}
                        <Lineups
                            homePlayers={homePlayers}
                            awayPlayers={guestPlayers}
                        />
                        <Substitutions
                            homePlayers={homeSubs}
                            awayPlayers={guestSubs}
                        />
                        <Coaches
                            homeCoach={homeCoach}
                            awayCoach={guestCoach}
                        />
                    </div>
                </>}
            </div >
        </PageTemplate>
    )
}